import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Layout, Button, Space, Avatar, Dropdown, Badge } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useMenuState } from '../../hooks/useMenuState';
import { findMenuItemByPath, getBreadcrumbItems } from '../../config/menuConfig';
import { LAYOUT_HEADER_HEIGHT, LAYOUT_SIDEBAR_COLLAPSED_WIDTH, LAYOUT_SIDEBAR_WIDTH } from '../../config/theme';
import { logoutAsync } from '../../redux/slices/authSlice';
import SidebarMenu from './SidebarMenu';
import MobileMenu from './MobileMenu';
import BottomNavBar from './BottomNavBar';
import './ResponsiveMenu.css';

const { Header } = Layout;

const ResponsiveMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoint();
  const { user } = useSelector(state => state.auth);
  const {
    sidebarCollapsed,
    mobileMenuVisible,
    selectedKeys,
    openKeys,
    toggleSidebar,
    toggleMobileMenu,
    closeMobileMenu,
    handleMenuSelect,
    handleSubMenuOpen,
    setSelectedKeys,
    setOpenKeys
  } = useMenuState();

  // 根据当前路径更新选中的菜单项
  useEffect(() => {
    const currentMenuItem = findMenuItemByPath(location.pathname);
    if (currentMenuItem) {
      setSelectedKeys([currentMenuItem.key]);
      
      // 获取面包屑路径，设置展开的菜单项
      const breadcrumbs = getBreadcrumbItems(location.pathname);
      const parentKeys = breadcrumbs.slice(0, -1).map(item => item.key);
      setOpenKeys(parentKeys);
    }
  }, [location.pathname, setSelectedKeys, setOpenKeys]);

  // 处理退出登录
  const handleLogout = () => {
    dispatch(logoutAsync()).then(() => {
      navigate('/auth/login');
    });
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <>
      {/* PC端侧边栏菜单 */}
      {!isMobile && (
        <SidebarMenu
          collapsed={sidebarCollapsed}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onSelect={handleMenuSelect}
          onOpenChange={handleSubMenuOpen}
        />
      )}

      {/* 顶部导航栏 */}
      <Header 
        className={`responsive-menu-header ${isMobile ? 'responsive-menu-header--mobile' : 'responsive-menu-header--desktop'}`}
        style={{
          left: isMobile ? 0 : (sidebarCollapsed ? LAYOUT_SIDEBAR_COLLAPSED_WIDTH : LAYOUT_SIDEBAR_WIDTH),
          height: LAYOUT_HEADER_HEIGHT,
        }}
      >
        <div className="responsive-menu-left">
          {!isMobile ? (
            <Button
              type="text"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSidebar}
              className="responsive-menu-toggle"
            />
          ) : (
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={toggleMobileMenu}
              className="responsive-menu-toggle"
            />
          )}
        </div>
        
        <div className="responsive-menu-right">
          <Space size="middle">
            <Badge count={0} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                className="responsive-menu-bell"
              />
            </Badge>
            
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <div className="responsive-menu-user">
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  src={user?.avatar}
                />
                {!isMobile && (
                  <span className="responsive-menu-username">
                    {user?.realName || user?.username}
                  </span>
                )}
              </div>
            </Dropdown>
          </Space>
        </div>
      </Header>

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <MobileMenu
          visible={mobileMenuVisible}
          onClose={closeMobileMenu}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onSelect={handleMenuSelect}
          onOpenChange={handleSubMenuOpen}
        />
      )}

      {/* 移动端底部导航栏 */}
      {isMobile && <BottomNavBar />}
    </>
  );
};

export default ResponsiveMenu;
